import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Upload, X, Building } from 'lucide-react';
import { toast } from 'sonner';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db, storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Organization } from '@/types/organization';



interface OrganizationFormProps {
  organization: Organization | null;
  onSuccess?: () => void;
  onClose: () => void;
}

export function OrganizationForm({ organization, onSuccess, onClose }: OrganizationFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>(organization?.logoUrl || '');
  const [removeCurrentLogo, setRemoveCurrentLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t, dir } = useLanguage();

  const formSchema = z.object({
    name: z.string().min(2, t('dashboard.admin.organizations.manage.form.validation.nameArabicMin')),
    nameEn: z.string().min(2, t('dashboard.admin.organizations.manage.form.validation.nameEnglishMin')),
    description: z.string().optional(),
    logoUrl: z.string().optional()
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: organization?.name || '',
      nameEn: organization?.nameEn || '',
      description: organization?.description || '',
      logoUrl: organization?.logoUrl || ''
    }
  });

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setLogoFile(file);
      setRemoveCurrentLogo(false);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setRemoveCurrentLogo(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadLogo = async (organizationId: string): Promise<string | null> => {
    if (!logoFile) return null;

    try {
      console.log('Starting logo upload for organization:', organizationId);
      const logoRef = ref(storage, `organizations/${organizationId}/logo.${logoFile.name.split('.').pop()}`);
      const snapshot = await uploadBytes(logoRef, logoFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('Logo uploaded successfully:', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw new Error('Failed to upload logo');
    }
  };

  const resetForm = () => {
    setLogoFile(null);
    setLogoPreview(organization?.logoUrl || '');
    setRemoveCurrentLogo(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!organization) return;

    try {
      setIsLoading(true);
      
      let logoUrl = organization.logoUrl;
      
      // Handle logo changes
      if (logoFile) {
        // Upload new logo
        logoUrl = await uploadLogo(organization.id);
      } else if (removeCurrentLogo) {
        // Remove current logo
        logoUrl = undefined;
      }
      
      const orgRef = doc(db, 'organizations', organization.id);
      
      // Prepare update data
      const updateData: any = {
        name: values.name,
        nameEn: values.nameEn,
        description: values.description || '',
        updatedAt: Timestamp.now()
      };
      
      // Only include logoUrl if it has changed
      if (logoFile || removeCurrentLogo) {
        updateData.logoUrl = logoUrl || null;
      }
      
      console.log('Updating organization with data:', updateData);
      
      await updateDoc(orgRef, updateData);
      
      console.log('Organization updated successfully');
      toast.success('Organization updated successfully');
      
      // Reset form state
      resetForm();
      
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error updating organization:', error);
      toast.error(error.message || 'Failed to update organization');
      
      // Don't reset form on error so user can retry
    } finally {
      console.log('Setting loading to false');
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" dir={dir}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.admin.organizations.manage.form.nameArabic')}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t('dashboard.admin.organizations.manage.form.nameArabicPlaceholder')} 
                  className={dir === 'rtl' ? 'text-right' : 'text-left'}
                  {...field} 
                />
              </FormControl>
              <FormMessage className={dir === 'rtl' ? 'text-right' : 'text-left'} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="nameEn"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.admin.organizations.manage.form.nameEnglish')}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t('dashboard.admin.organizations.manage.form.nameEnglishPlaceholder')} 
                  className={dir === 'rtl' ? 'text-right' : 'text-left'}
                  {...field} 
                />
              </FormControl>
              <FormMessage className={dir === 'rtl' ? 'text-right' : 'text-left'} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.admin.organizations.manage.form.description')}</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder={t('dashboard.admin.organizations.manage.form.descriptionPlaceholder')}
                  className={`resize-none ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                  {...field}
                />
              </FormControl>
              <FormMessage className={dir === 'rtl' ? 'text-right' : 'text-left'} />
            </FormItem>
          )}
        />

        {/* Logo Upload */}
        <div className="space-y-2">
          <FormLabel className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.admin.organizations.manage.form.logo')}</FormLabel>
                      <div className={`flex ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'} items-center gap-4`}>
              <div className="h-16 min-w-16 flex items-center justify-center">
                {logoPreview ? (
                  <img 
                    src={logoPreview} 
                    alt="Organization logo" 
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <Building className="h-8 w-8 text-gray-400" />
                )}
              </div>
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
              >
                <Upload className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                {t('dashboard.admin.organizations.manage.form.uploadLogo')}
              </Button>
              {(logoPreview || logoFile) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={removeLogo}
                  disabled={isLoading}
                >
                  <X className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  {t('dashboard.admin.organizations.manage.form.removeLogo')}
                </Button>
              )}
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoSelect}
            className="hidden"
          />
        </div>

        <div className={`flex ${dir === 'rtl' ? 'justify-start' : 'justify-end'} gap-2`}>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {t('dashboard.admin.organizations.manage.form.cancel')}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className={`${dir === 'rtl' ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} />
                {t('common.saving')}
              </>
            ) : (
              t('dashboard.admin.organizations.manage.form.saveChanges')
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}